import { practiceDay, practiceWeek, STREAK_SESSION_SECONDS } from "./practiceMetrics.js";
/** Practice and lesson awards. All durations are measured in seconds. */
export type AwardShape = "circle" | "square" | "shield" | "hexagon" | "star";
export type AwardMetal = "silver" | "gold" | "chrome" | "white";
export interface AwardDefinition {
  key: string; title: string; description: string; shape: AwardShape;
  icon: "bug" | "welcome" | "practice" | "book" | "resource" | "calendar" | "collection" | "clock" | "hourglass" | "return" | "target" | "feedback" | "shape-only" | "check" | "reflection" | "metronome" | "recording" | "creative" | "month" | "legend" | "year" | "instrument" | "perfect-week" | "weekly-hour" | "checked-calendar";
  colour: string; metal: AwardMetal; milestone?: number;
  lessonInstrument?: LessonAwardInstrument;
  lessonMilestone?: number;
  /** Family display cards distinguish otherwise identical domain milestones. */
  lessonDomain?: LessonAwardDomain;
  /** A calendar-month display card keeps the existing month/year receipt. */
  receiptKey?: string;
  month?: number;
}
const award = (key: string, title: string, description: string, shape: AwardShape, icon: AwardDefinition["icon"], colour: string,
  presentation: { metal?: AwardMetal; milestone?: number } = {}): AwardDefinition => ({ key, title, description, shape, icon, colour, metal: "silver", ...presentation });
export const PRACTICE_LEGEND_MILESTONES = [10, 25, 50, 75, 100, 150, 200] as const;
export const TUTOR_RECOGNITIONS = ["personal-breakthrough", "piece-milestone", "creative-practice"] as const;
export type TutorRecognition = typeof TUTOR_RECOGNITIONS[number];
export const LESSON_ATTENDANCE_MILESTONES = [
  1, 10, 25, 50, 75, 100, 125, 150, 175, 200, 225, 250, 275, 300,
  325, 350, 375, 400, 425, 450, 475, 500, 525, 550, 575, 600,
] as const;
/** Current WMF lesson subjects; keyboard attendance belongs to Piano. */
export const LESSON_AWARD_INSTRUMENTS = [
  { key: "drums", name: "Drums", lessonName: "Drum" },
  { key: "piano", name: "Piano", lessonName: "Piano" },
  { key: "guitar", name: "Guitar", lessonName: "Guitar" },
  { key: "bass", name: "Bass", lessonName: "Bass" },
  { key: "singing", name: "Singing", lessonName: "Singing" },
  { key: "violin", name: "Violin", lessonName: "Violin" },
  { key: "saxophone", name: "Saxophone", lessonName: "Saxophone" },
  { key: "flute", name: "Flute", lessonName: "Flute" },
  { key: "ukulele", name: "Ukulele", lessonName: "Ukulele" },
  { key: "mandolin", name: "Mandolin", lessonName: "Mandolin" },
  { key: "music-theory", name: "Music theory", lessonName: "Music Theory" },
] as const;
export type LessonAwardInstrument = typeof LESSON_AWARD_INSTRUMENTS[number]["key"];
export const lessonMilestoneLabel = (milestone: number) => `${milestone}${milestone === 1 ? "st" : "th"}`;
const lessonAwards: AwardDefinition[] = LESSON_AWARD_INSTRUMENTS.flatMap(instrument => LESSON_ATTENDANCE_MILESTONES.map(milestone => ({
  ...award(`lessons-${instrument.key}-${milestone}`, `${lessonMilestoneLabel(milestone)} ${instrument.lessonName} Lesson`,
    `Attend ${milestone} ${instrument.lessonName.toLowerCase()} ${milestone === 1 ? "lesson" : "lessons"}, marked Present or Late by your tutor. School and private lesson counts are separate.`,
    "shield", "instrument", "#383a40", { metal: "white" }),
  lessonInstrument: instrument.key, lessonMilestone: milestone,
})));
export const AWARD_CATALOGUE: AwardDefinition[] = [
  award("bug-hunter", "Bug Hunter", "Report a problem from your student account that the Hub owner confirms is a bug needing a fix.", "shield", "bug", "#101015", { metal: "gold" }),
  award("hello-hub", "Hello Hub", "Visit your Student Hub for the first time.", "circle", "welcome", "#702a9e"),
  award("first-practice", "1st Practice", "Log your first practice session.", "square", "practice", "#00a5b5"),
  ...[15, 30, 60].map(minutes => award(`first-session-${minutes}`, `First ${minutes} Minute Practice`, `Log your first individual practice session lasting at least ${minutes} minutes.`, "circle", "clock", "#2271b9", { milestone: minutes })),
  award("lesson-review", "Lesson Review", "Open the published notes from three different lessons.", "circle", "book", "#075ac1"),
  award("resource-explorer", "Resource Explorer", "Log practice linked to an assigned resource.", "hexagon", "resource", "#258372"),
  award("consistent-practice", "Consistent Practice", "Meet your personal weekly target in four separate weeks.", "shield", "calendar", "#008f59"),
  ...[10, 25, 50, 100].map(n => award(`practice-collector-${n}`, `Practice Collector ${n}`, `Log ${n} practice sessions.`, "circle", "collection", "#0753b5", { milestone: n })),
  ...[1, 5, 10, 25].map(n => award(`practice-hours-${n}`, `${n} ${n === 1 ? "Hour" : "Hours"} of Practice`, `Log ${n} total ${n === 1 ? "hour" : "hours"} of practice.`, "circle", "hourglass", "#a16b12", { milestone: n })),
  award("welcome-back", "Welcome Back", "Log practice after at least 14 days without an entry. Earned once.", "circle", "return", "#101015"),
  ...[1, 5, 10, 25].map(n => award(`goal-getter-${n}`, `Goal Getter ${n}`, `Have ${n} assigned practice ${n === 1 ? "task" : "tasks"} confirmed complete by your tutor.`, "square", "target", "#00a5b5", { milestone: n })),
  award("feedback-in-action", "Feedback in Action", "Log practice linked to a published lesson's practice task.", "square", "feedback", "#307ca8"),
  award("piece-milestone", "Repertoire Star", "For exceptional progress when learning a song or repertoire piece.", "star", "shape-only", "#be860c", { metal: "gold" }),
  award("personal-breakthrough", "Task Master", "For outstanding effort and dedication to tutor-assigned practice.", "shield", "check", "#bd1665", { metal: "gold" }),
  award("practice-reflection", "Practice Reflection", "Add a short reflection to five practice sessions.", "square", "reflection", "#6c529e"),
  award("metronome-practice", "Metronome Practice", "Log five measured practice sessions with the metronome.", "shield", "metronome", "#8bb400"),
  award("first-recording", "First Recording", "Share an audio or video practice recording with your tutor.", "square", "recording", "#df4f48"),
  award("creative-practice", "Practice Pioneer", "For demonstrating independence, curiosity and initiative through self-directed musical practice.", "hexagon", "creative", "#7840b4", { metal: "gold" }),
  award("monthly-challenge", "Monthly Challenge", "Log practice on eight different days in a calendar month.", "hexagon", "checked-calendar", "#7840b4", { metal: "gold" }),
  award("weekly-hour", "Weekly Hour", "Log at least 60 minutes of practice in a Monday-to-Sunday week.", "shield", "weekly-hour", "#3a7b8a"),
  award("perfect-week", "Perfect Week", "Log a session lasting at least 10 minutes on each of seven consecutive days.", "shield", "perfect-week", "#277f56"),
  award("perfect-month", "Perfect Month", "Log a session lasting at least 10 minutes every day of a calendar month.", "shield", "month", "#6e4bb1", { metal: "gold" }),
  award("practice-legend", "Practice Legend", "Your tutor confirms a practice task complete. Each task earns its own award.", "shield", "legend", "#9a6d15", { metal: "gold" }),
  ...PRACTICE_LEGEND_MILESTONES.map(n => award(`practice-legend-${n}`, `Practice Legend ${n}`, `Have ${n} different practice tasks confirmed complete by your tutor.`, "shield", "legend", "#9a6d15", { metal: "gold", milestone: n })),
  award("perfect-year", "The 365", "Log a session of at least 10 minutes every day of a calendar year, including 29 February in a leap year.", "shield", "year", "#111116", { metal: "chrome" }),
  ...lessonAwards,
];

/** One JavaScript parser classifies the distinct service labels read by the
 * production SQL projection. SQL joins the resulting exact labels; it never
 * reinterprets a JavaScript regular expression. */
const lessonAliases: Record<LessonAwardInstrument, readonly string[]> = {
  drums: ["drum", "drums"], piano: ["piano", "keyboard"], guitar: ["guitar"],
  bass: ["bass", "bass guitar"], singing: ["singing", "vocal", "voice"],
  violin: ["violin"], saxophone: ["saxophone", "sax"], flute: ["flute"],
  ukulele: ["ukulele"], mandolin: ["mandolin"], "music-theory": ["music theory", "theory"],
};
const lessonSubjectMatchers = LESSON_AWARD_INSTRUMENTS.map(({ key }) => {
  const subject = `(${lessonAliases[key].join("|")})`;
  const duration = "[0-9]+(\\.[0-9]+)?[ -]*(minute|min|hour|hr)s?";
  const location = "(wirral|calday|west kirby)";
  return { instrument: key, pattern: new RegExp(`^((online +)?(${duration}[ -]+)?${subject}( +(lessons?|coaching|tuition))?( +1:1)?( *-? *${duration}| *\\(${duration}\\))?|${subject} *- *[0-9]+-lesson block *\\(${duration}\\))( *- *${location}| *\\(${location}\\))?$`, "i") };
});
export function lessonAwardInstrument(serviceName: string | null, domain?: LessonAwardDomain): LessonAwardInstrument | null {
  const name = serviceName?.normalize("NFKC").trim().replace(/[–—]/g, "-").replace(/\s+/g, " ");
  // Matt confirmed this otherwise generic School service is Drums on 16 October 2026.
  if (domain === "school" && name?.toLowerCase() === "30 minute school lesson (west kirby)") return "drums";
  return name ? lessonSubjectMatchers.find(({ pattern }) => pattern.test(name))?.instrument ?? null : null;
}

export type LessonAwardDomain = "private" | "school";
export interface LessonAwardCandidate extends AwardCandidate { domain: LessonAwardDomain; sourceId: string }
export interface LessonAttendanceProgress { domain: LessonAwardDomain; instrument: LessonAwardInstrument; count: number }
export interface LessonAttendanceSummary { instruments: LessonAttendanceProgress[]; excludedSessions: number }
export interface LessonAttendanceEvaluation extends LessonAttendanceSummary { awards: LessonAwardCandidate[] }

/** Keep earned receipts and the next milestone independently in each teaching
 * domain. A private 10th medal cannot satisfy or conceal the School 10th. */
export function studentAwardCatalogue(catalogue: readonly AwardDefinition[], earned: readonly EarnedAward[], attendance: LessonAttendanceSummary): AwardDefinition[] {
  const counts = new Map(attendance.instruments.map(value => [`${value.domain}:${value.instrument}`, value.count]));
  const result: AwardDefinition[] = [];
  for (const definition of catalogue) {
    if (!definition.lessonInstrument) { result.push(definition); continue; }
    for (const domain of ["private", "school"] as const) {
      const count = counts.get(`${domain}:${definition.lessonInstrument}`);
      const next = count === undefined ? undefined : LESSON_ATTENDANCE_MILESTONES.find(value => value > count);
      const isEarned = earned.some(record => record.key === definition.key && record.domain === domain);
      if (!isEarned && definition.lessonMilestone !== next) continue;
      result.push({ ...definition, key: `${definition.key}-${domain}`, receiptKey: definition.key, lessonDomain: domain,
        title: `${definition.title} · ${domain === "private" ? "Private" : "School"}` });
    }
  }
  return result;
}

export const PERFECT_MONTH_NAMES = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"] as const;

/** Twelve independently collectible cards, without changing saved receipts or
 * issuing duplicate awards for an already-earned calendar month/year. */
export function displayAwardCatalogue(catalogue: readonly AwardDefinition[]): AwardDefinition[] {
  return catalogue.flatMap(definition => definition.key !== "perfect-month" ? [definition] : PERFECT_MONTH_NAMES.map((month, index) => ({
    ...definition, key: `perfect-month-${String(index + 1).padStart(2, "0")}`, receiptKey: definition.key, month: index + 1,
    title: `Perfect ${month}`, description: `Log a session of at least 10 minutes every day in ${month}.`,
  })));
}

export function awardMatchesRecord(definition: AwardDefinition, record: { key: string; instance: string; domain?: LessonAwardDomain }): boolean {
  return record.key === (definition.receiptKey ?? definition.key)
    && (definition.lessonDomain === undefined || definition.lessonDomain === record.domain)
    && (definition.month === undefined || (/^\d{4}-(0[1-9]|1[0-2])$/.test(record.instance) && Number(record.instance.slice(5)) === definition.month));
}

/** Recognition keys remain stable for saved drafts and receipts. Display the
 * current name without rewriting the original receipt or calendar editions. */
export function awardDisplayTitle(record: { key: string; title: string }): string {
  return (TUTOR_RECOGNITIONS as readonly string[]).includes(record.key)
    ? AWARD_CATALOGUE.find(definition => definition.key === record.key)!.title : record.title;
}

export interface AwardCandidate { key: string; instance: string; title: string; earnedAt: string }
export interface EarnedAward extends AwardCandidate { id: string; reason: string | null; domain?: LessonAwardDomain }
export interface AwardSession {
  id: string; startedAt: string; durationSeconds: number; notes?: string | null;
  metronome?: boolean; recording?: boolean; resource?: boolean; feedback?: boolean;
}
export interface WeeklyPracticeTarget { week: string; minutes: number }
export interface AwardStats {
  totalSeconds?: number; thisWeekSeconds?: number;
  sessions: number; totalMinutes: number; thisWeekMinutes: number;
  currentStreak: number; longestStreak: number; lastQualifyingDay: string | null;
  thisMonthDays: number; thisMonthQualifyingDays: number; thisMonthCalendarDays: number;
  targetWeeks: number; completedTasks: number;
}
export interface StudentAwardsSummary {
  enabled: true; timezone: string; weeklyTargetMinutes: number;
  nextWeeklyTarget?: WeeklyPracticeTarget;
  stats: AwardStats; catalogue: AwardDefinition[]; earned: EarnedAward[]; lessonAttendance: LessonAttendanceSummary;
}

const DAY = 86_400_000;
const weekTitleFormat = new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "short", year: "numeric", timeZone: "UTC" });
const ordinal = (day: string) => Date.parse(`${day}T00:00:00Z`) / DAY;
const fromOrdinal = (n: number) => new Date(n * DAY).toISOString().slice(0, 10);
export function awardDay(date: Date | string, timeZone: string): string {
  return practiceDay(date, timeZone);
}
export function awardWeek(day: string): string {
  return practiceWeek(day);
}
export function weeklyTargetFor(week: string, targets: readonly WeeklyPracticeTarget[]): number {
  return [...targets].filter(target => target.week <= week).sort((a, b) => b.week.localeCompare(a.week))[0]?.minutes ?? 60;
}

/** Calendar-day arithmetic deliberately happens after timezone conversion.
 * Focus segments never enter this input: a completed overall session counts once. */
export function evaluateStudentAwards(input: {
  sessions: readonly AwardSession[]; now: Date; timeZone: string;
  targets?: readonly WeeklyPracticeTarget[]; viewedNotes?: number;
  completedTasks?: number; helloHub?: boolean;
}): { awards: AwardCandidate[]; stats: AwardStats } {
  const { now, timeZone } = input;
  const today = awardDay(now, timeZone);
  const thisWeek = awardWeek(today);
  const thisMonth = today.slice(0, 7);
  const sessions = [...new Map(input.sessions.map(session => [session.id, session])).values()]
    .filter(session => Number.isFinite(Date.parse(session.startedAt)) && Date.parse(session.startedAt) <= now.getTime()
      && Number.isFinite(session.durationSeconds) && session.durationSeconds > 0)
    .sort((a, b) => a.startedAt.localeCompare(b.startedAt) || a.id.localeCompare(b.id));
  const definitions = new Map(AWARD_CATALOGUE.map(value => [value.key, value]));
  const awards = new Map<string, AwardCandidate>();
  function earn(key: string, at: string, instance = "once", title?: string) {
    const identity = `${key}:${instance}`;
    if (!awards.has(identity)) awards.set(identity, { key, instance, title: title ?? definitions.get(key)!.title, earnedAt: at });
  }
  if (input.helloHub) earn("hello-hub", now.toISOString());
  if ((input.viewedNotes ?? 0) >= 3) earn("lesson-review", now.toISOString());
  const weeks = new Map<string, number>();
  const months = new Map<string, Set<string>>();
  const qualifying = new Map<string, string>();
  let totalSeconds = 0, reflections = 0, metronome = 0;
  let previousDay: string | null = null;
  sessions.forEach((session, index) => {
    const at = new Date(session.startedAt).toISOString();
    const day = awardDay(at, timeZone), week = awardWeek(day), month = day.slice(0, 7);
    if (index === 0) earn("first-practice", at);
    for (const minutes of [15, 30, 60]) if (session.durationSeconds >= minutes * 60) earn(`first-session-${minutes}`, at);
    for (const n of [10, 25, 50, 100]) if (index + 1 >= n) earn(`practice-collector-${n}`, at);
    totalSeconds += session.durationSeconds;
    for (const hours of [1, 5, 10, 25]) if (totalSeconds >= hours * 3600) earn(`practice-hours-${hours}`, at);
    const weekSeconds = (weeks.get(week) ?? 0) + session.durationSeconds;
    weeks.set(week, weekSeconds);
    if (weekSeconds >= 3600) earn("weekly-hour", at, week, `Weekly Hour · week of ${weekTitleFormat.format(new Date(`${week}T12:00:00Z`))}`);
    const days = months.get(month) ?? new Set<string>();
    days.add(day); months.set(month, days);
    const monthLabel = new Intl.DateTimeFormat("en-GB", { month: "long", year: "numeric", timeZone: "UTC" }).format(new Date(`${month}-01T12:00:00Z`));
    if (days.size >= 8) earn("monthly-challenge", at, month, `Monthly Challenge · ${monthLabel}`);
    if (session.durationSeconds >= STREAK_SESSION_SECONDS && !qualifying.has(day)) qualifying.set(day, at);
    if (previousDay && ordinal(day) - ordinal(previousDay) >= 14) earn("welcome-back", at);
    previousDay = day;
    if (session.notes?.trim()) reflections++;
    if (reflections >= 5) earn("practice-reflection", at);
    if (session.metronome) metronome++;
    if (metronome >= 5) earn("metronome-practice", at);
    if (session.recording) earn("first-recording", at);
    if (session.resource) earn("resource-explorer", at);
    if (session.feedback) earn("feedback-in-action", at);
  });
  let run = 0, longestStreak = 0, previous: string | null = null;
  const qualifyingMonths = new Map<string, number>();
  const qualifyingYears = new Map<string, number>();
  for (const [day, at] of [...qualifying].sort(([a], [b]) => a.localeCompare(b))) {
    run = previous && ordinal(day) === ordinal(previous) + 1 ? run + 1 : 1;
    longestStreak = Math.max(longestStreak, run);
    if (run >= 7) earn("perfect-week", at);
    previous = day;
    const month = day.slice(0, 7);
    qualifyingMonths.set(month, (qualifyingMonths.get(month) ?? 0) + 1);
    const [year, monthNumber] = month.split("-").map(Number);
    const daysInMonth = new Date(Date.UTC(year!, monthNumber!, 0)).getUTCDate();
    if (qualifyingMonths.get(month) === daysInMonth) {
      const monthLabel = new Intl.DateTimeFormat("en-GB", { month: "long", year: "numeric", timeZone: "UTC" }).format(new Date(`${month}-01T12:00:00Z`));
      earn("perfect-month", at, month, `Perfect ${monthLabel}`);
    }
    const calendarYear = day.slice(0, 4);
    qualifyingYears.set(calendarYear, (qualifyingYears.get(calendarYear) ?? 0) + 1);
    const daysInYear = (Date.UTC(Number(calendarYear) + 1, 0, 1) - Date.UTC(Number(calendarYear), 0, 1)) / DAY;
    if (qualifyingYears.get(calendarYear) === daysInYear) earn("perfect-year", at, calendarYear, `The 365 · ${calendarYear}`);
  }
  const lastQualifyingDay = previous;
  // Today's practice may still happen. Yesterday's run remains current until
  // the next local midnight, without labelling a missed older day as current.
  const currentStreak = lastQualifyingDay && ordinal(today) - ordinal(lastQualifyingDay) <= 1 ? run : 0;
  const targetWeeks = [...weeks].filter(([week, seconds]) => seconds >= weeklyTargetFor(week, input.targets ?? []) * 60).length;
  if (targetWeeks >= 4) earn("consistent-practice", now.toISOString());
  const completedTasks = input.completedTasks ?? 0;
  for (const n of [1, 5, 10, 25]) if (completedTasks >= n) earn(`goal-getter-${n}`, now.toISOString());
  for (const n of PRACTICE_LEGEND_MILESTONES) if (completedTasks >= n) earn(`practice-legend-${n}`, now.toISOString());
  const [year, month] = thisMonth.split("-").map(Number);
  return { awards: [...awards.values()], stats: {
    sessions: sessions.length, totalSeconds, thisWeekSeconds: weeks.get(thisWeek) ?? 0,
    totalMinutes: Math.floor(totalSeconds / 60), thisWeekMinutes: Math.floor((weeks.get(thisWeek) ?? 0) / 60),
    currentStreak, longestStreak, lastQualifyingDay,
    thisMonthDays: months.get(thisMonth)?.size ?? 0, thisMonthQualifyingDays: qualifyingMonths.get(thisMonth) ?? 0,
    thisMonthCalendarDays: new Date(Date.UTC(year!, month!, 0)).getUTCDate(), targetWeeks, completedTasks,
  } };
}
